import type { ReduxState, ReduxSagaModel, InvokeResult } from './types';
import type { SaveCurrentAction as SaveCurrentWorkspaceAction } from './workspace';
import moment from 'moment';
import { history, resetAllModelStatesOnWorkspaceSwitch } from 'umi';
import { UserRole } from '@/api';
import { AmplifyFetchAuthSession } from '@/services/login';
import Organization from '@/services/organization';
import { sgcall, sgselect } from '@/utils/reduxSaga';
import { setAuthority } from '@/utils/authority';
import { handleUnknownError, handleUserError } from '@/utils/handleError';
import { logger } from '@/utils/logger';
import { assertIsDefined, assertIsOrganization, assertIsString } from '@/utils/typeChecks';
import { LANDING_STATE } from '@/utils/landing';

const ns = 'app.models.organization';

const MRU_ORG_KEY = 'beem-recently-used-organization';
const MRU_WS_BY_ORG = 'beem-last-used-workspace-';
const ORG_ID_BY_SLUG = 'beem-organization-id-by-slug-';

export interface State {
  currentOrganization: string | null;
  currentWorkspace: string | null;
  /**
   * The user's role in the current workspace, saved in the same action as the workspace so the
   * two never disagree. Read it through `useRoles()`; `getAuthority()` (localStorage) is the copy
   * antd's `Authorized` reads, and the fallback before the first switch.
   */
  currentRole: UserRole | null;
  current: Organization | null;
  invokeSetWarehouseRetentionResult: InvokeResult<null> | null;
  invokeCreateFivetranGroupIdIfNotExistResult: InvokeResult<string> | null;
  invokeRemoveUserAccessResult: InvokeResult<null> | null;
  dpuAndDatalakeUsages: {
    dpu: number;
    datalake: number;
  } | null;
  dpuAllowanceAndCycle: {
    allowance: number;
    billingCycleDate: number;
  } | null;
}

function getInitialState(): State {
  return {
    currentOrganization: null,
    currentWorkspace: null,
    currentRole: null,
    current: null,
    invokeSetWarehouseRetentionResult: null,
    invokeCreateFivetranGroupIdIfNotExistResult: null,
    invokeRemoveUserAccessResult: null,
    dpuAndDatalakeUsages: null,
    dpuAllowanceAndCycle: null,
  };
}

interface SaveCurrentOrganizationAndWorkspaceAction {
  type: 'saveCurrentOrganizationAndWorkspace';
  payload: Pick<State, 'currentOrganization' | 'currentWorkspace' | 'currentRole'>;
}

interface SaveCurrentAction {
  type: 'saveCurrent';
  payload: Pick<State, 'current'>;
}

interface SaveDpuAndDatalakeUsages {
  type: 'saveDpuAndDatalakeUsages';
  payload: Pick<State, 'dpuAndDatalakeUsages'>;
}
interface SaveDpuAllowanceAndCycle {
  type: 'saveDpuAllowanceAndCycle';
  payload: Pick<State, 'dpuAllowanceAndCycle'>;
}

interface SaveInvokeSetWarehouseRetentionResultAction {
  type: 'saveInvokeSetWarehouseRetentionResult';
  payload: Pick<State, 'invokeSetWarehouseRetentionResult'>;
}

interface SaveInvokeCreateFivetranGroupIdIfNotExistResultAction {
  type: 'saveInvokeCreateFivetranGroupIdIfNotExistResult';
  payload: Pick<State, 'invokeCreateFivetranGroupIdIfNotExistResult'>;
}

interface SaveInvokeRemoveUserAccessResultAction {
  type: 'saveInvokeRemoveUserAccessResult';
  payload: Pick<State, 'invokeRemoveUserAccessResult'>;
}

export type OrganizationUseEffect = {
  type: 'organization/use';
  payload: {
    organizationSlug?: string;
    organizationId?: string;
    workspaceId?: string;
  };
};

const OrganizationModel: ReduxSagaModel<
  State,
  {
    saveCurrentOrganizationAndWorkspace: SaveCurrentOrganizationAndWorkspaceAction;
    saveCurrent: SaveCurrentAction;
    saveInvokeSetWarehouseRetentionResult: SaveInvokeSetWarehouseRetentionResultAction;
    saveInvokeCreateFivetranGroupIdIfNotExistResult: SaveInvokeCreateFivetranGroupIdIfNotExistResultAction;
    saveInvokeRemoveUserAccessResult: SaveInvokeRemoveUserAccessResultAction;
    saveDpuAndDatalakeUsages: SaveDpuAndDatalakeUsages;
    saveDpuAllowanceAndCycle: SaveDpuAllowanceAndCycle;
  }
> = {
  namespace: 'organization',
  state: getInitialState(),
  effects: {
    *use({ payload }: { payload: OrganizationUseEffect['payload'] }, { put }) {
      logger.debug({ label: 'app.models.organization.use', message: payload });
      const { organizationSlug } = payload;
      let { organizationId, workspaceId } = payload;

      const organizations = yield* sgselect((s) => s.user.organizations);
      assertIsDefined(organizations, 'BEEM250406121936');
      let workspaces = yield* sgselect((s) => s.user.workspaces);
      assertIsDefined(workspaces, 'BEEM250406121937');

      // get org id from org slug
      if (organizationSlug) {
        const key = ORG_ID_BY_SLUG + organizationSlug;
        organizationId = localStorage.getItem(key) ?? '';
        // no need to remove the key because the value never changes
        if (!organizationId) {
          const organization = yield* sgcall(() =>
            Organization.getOrganizationBySlug(organizationSlug),
          );
          if (organization) {
            organizationId = organization.id;
            localStorage.setItem(key, organizationId);
          }
        }
      }

      // if org id is not provided, get it from local storage
      // if still no org id, then use the default org if only one org
      // else redirect to select org page
      // else make sure the org id is valid
      if (!organizationId) {
        organizationId = localStorage.getItem(MRU_ORG_KEY) ?? '';
        // no need to remove the key because we check below if it's valid
      }
      let needSelectOrg = false;
      if (!organizationId) {
        if (organizations.length === 1) {
          organizationId = organizations[0].id;
        } else {
          needSelectOrg = true;
        }
      } else {
        needSelectOrg = !organizations.find((o) => o.id === organizationId);
      }
      if (needSelectOrg) {
        if (organizationSlug) {
          // this should only happens in dev env
          localStorage.removeItem(ORG_ID_BY_SLUG + organizationSlug);
        }
        history.push('/account/select-organization');
        return;
      }

      // if workspace id is not provided, get it from local storage (by org id)
      // make sure the workspace id is valid (in the same org)
      // if still no workspace id, then use the 1st workspace in the list
      if (!workspaceId) {
        const key = MRU_WS_BY_ORG + organizationId;
        workspaceId = localStorage.getItem(key) ?? '';
        localStorage.removeItem(key);
      }
      if (workspaceId) {
        let found = workspaces.find(
          (w) => w.organization.id === organizationId && w.id === workspaceId,
        );
        if (!found) {
          // The workspace list may be stale (e.g. workspace was just created).
          // Re-fetch and retry before discarding the workspaceId.
          const freshWorkspaces = yield* sgcall(Organization.getWorkspacesOfCurrentUser);
          if (freshWorkspaces) {
            workspaces = freshWorkspaces;
            found = workspaces.find(
              (w) => w.organization.id === organizationId && w.id === workspaceId,
            );
          }
          if (!found) workspaceId = undefined;
        }
      }
      if (!workspaceId) {
        const found = workspaces.find((w) => w.organization.id === organizationId);
        assertIsDefined(found, 'BEEM211105120230');
        workspaceId = found.id;
      }

      const organization = organizations.find((o) => o.id === organizationId);
      assertIsDefined(organization, 'BEEM250406082949');
      const workspace = workspaces.find((w) => w.id === workspaceId);
      assertIsDefined(workspace, 'BEEM250406082950');
      // The role is resolved before the switch and saved with it, in one action, so no render
      // pairs the new workspace with the old role or the other way round.
      const cognitoUser = yield* sgcall(AmplifyFetchAuthSession);
      const cognitoClaim =
        cognitoUser.getSignInUserSession()?.getAccessToken().payload['beem:orgAdmin'] ?? [];
      let authority: UserRole;
      if (cognitoClaim.includes(organizationId)) {
        authority = UserRole.ADMIN;
      } else {
        // for now we read the role from WorkspaceUser record, but in the future we may want to
        // read it from Cognito claim instead for security reasons (right now EDITOR and VIEWER
        // have the same auth rules in graphql schema, we only use them to block UI actions)
        authority = yield* sgcall(() => workspace.getNonAdminMemberRole(cognitoUser.username));
      }
      // antd's `Authorized` reads its copy from localStorage. Written synchronously just before the
      // put, with no yield between them, so it too changes in step with the workspace.
      try {
        setAuthority(authority);
      } catch (e) {
        // Storage refused (quota, a private window): the store still gets the role below.
        logger.warn({ label: `${ns}.use.setAuthority`, message: e });
      }
      yield put<SaveCurrentOrganizationAndWorkspaceAction>({
        type: 'saveCurrentOrganizationAndWorkspace',
        payload: {
          currentOrganization: organizationId,
          currentWorkspace: workspaceId,
          currentRole: authority,
        },
      });

      // Clear all workspace-scoped state to prevent data leaking across workspaces
      yield* resetAllModelStatesOnWorkspaceSwitch(put);

      yield put<SaveCurrentAction>({ type: 'saveCurrent', payload: { current: organization } });

      yield put<SaveCurrentWorkspaceAction>({
        type: 'workspaces/saveCurrent',
        payload: { current: workspace },
      });

      localStorage.setItem(MRU_ORG_KEY, organizationId);
      localStorage.setItem(MRU_WS_BY_ORG + organizationId, workspaceId);
      if (organization.slug !== payload.organizationSlug || workspaceId !== payload.workspaceId) {
        // A fresh login (dispatched from /login-success with no explicit org/ws) lands on the
        // AI Insights home; an explicit workspace switch keeps the role-based default. Either way
        // the navigation is tagged so BasicLayout can send it on to Home when the workspace has
        // one and the `workspace-home` flag is on (useHomeLanding). The flag can't be read here.
        const isFreshLogin = !payload.organizationSlug;
        const landing = isFreshLogin
          ? 'assistant'
          : authority !== UserRole.VIEWER
          ? 'catalog'
          : 'dashboard';
        history.push(`/${organization.slug}/${workspaceId}/${landing}`, LANDING_STATE);
      }
    },

    *get(_, { put }) {
      try {
        const organizationId = yield* sgselect((s) => s.organization.currentOrganization);
        assertIsDefined(organizationId, 'BEEM211105120628');
        const organization = yield* sgcall(() => Organization.getOrganizationById(organizationId));
        logger.debug({ label: `${ns}.get.current`, message: organization });
        yield put<SaveCurrentAction>({ type: 'saveCurrent', payload: { current: organization } });
      } catch (e) {
        logger.error({ label: `${ns}.get.error`, message: e });
        yield put<SaveCurrentAction>({ type: 'saveCurrent', payload: { current: null } });
      }
    },

    *setWarehouseRetention(
      { payload: { daysToRetainData } }: { payload: { daysToRetainData: number } },
      { call, put },
    ) {
      try {
        const organization = yield* sgselect((s) => s.organization.current);
        assertIsOrganization(organization, 'BEEM220712161321');
        yield call(organization.setWarehouseRetention, daysToRetainData);
        yield put<SaveInvokeSetWarehouseRetentionResultAction>({
          type: 'saveInvokeSetWarehouseRetentionResult',
          payload: { invokeSetWarehouseRetentionResult: { success: true, data: null } },
        });
      } catch (e) {
        yield put<SaveInvokeSetWarehouseRetentionResultAction>({
          type: 'saveInvokeSetWarehouseRetentionResult',
          payload: {
            invokeSetWarehouseRetentionResult: {
              success: false,
              error: handleUserError(e, `${ns}.setWarehouseRetention.error`),
            },
          },
        });
      }
    },

    *createFivetranGroupIdIfNotExist(_, { put, select }) {
      try {
        const organizationId = yield select<ReduxState>((s) => s.organization.currentOrganization);
        assertIsString(organizationId, 'BEEM230912135627');
        const organization = yield* sgcall(() => Organization.getOrganizationById(organizationId));
        const fivetranGroupId = yield* sgcall(organization.createFivetranGroupIdIfNotExist);
        yield put<SaveInvokeCreateFivetranGroupIdIfNotExistResultAction>({
          type: 'saveInvokeCreateFivetranGroupIdIfNotExistResult',
          payload: {
            invokeCreateFivetranGroupIdIfNotExistResult: { success: true, data: fivetranGroupId },
          },
        });
      } catch (e) {
        yield put<SaveInvokeCreateFivetranGroupIdIfNotExistResultAction>({
          type: 'saveInvokeCreateFivetranGroupIdIfNotExistResult',
          payload: {
            invokeCreateFivetranGroupIdIfNotExistResult: {
              success: false,
              error: handleUserError(e, `${ns}.createFivetranGroupIdIfNotExist.error`),
            },
          },
        });
      }
    },

    *removeUserAccess({ payload: { userId } }: { payload: { userId: string } }, { put }) {
      try {
        const current = yield* sgselect((s) => s.organization.current, 'BEEM268307275109');
        yield* sgcall(() => current.removeUserFromAllWorkspaces(userId));
        yield* sgcall(() => current.removeUser(userId));
        yield put<SaveInvokeRemoveUserAccessResultAction>({
          type: 'saveInvokeRemoveUserAccessResult',
          payload: {
            invokeRemoveUserAccessResult: { success: true, data: null },
          },
        });
      } catch (e) {
        yield put<SaveInvokeRemoveUserAccessResultAction>({
          type: 'saveInvokeRemoveUserAccessResult',
          payload: {
            invokeRemoveUserAccessResult: {
              success: false,
              error: handleUserError(e, `${ns}.removeUserAccess.error`),
            },
          },
        });
      }
    },

    *getDpuAndDatalakeUsages(_, { select, put }) {
      const ns = 'app.models.organization.getDpuAndDatalakeUsages';
      try {
        const organizationId = yield select<ReduxState>((s) => s.organization.currentOrganization);
        assertIsString(organizationId, 'BEEM230912135627');
        const dpuAllowanceAndCycle = yield* sgcall(() =>
          Organization.getDpuAllowanceAndBilling(organizationId),
        );
        const usages = yield* sgcall(() =>
          Organization.getDpuAndDatalakeUsagesByOrganizationId(
            organizationId,
            moment()
              .date(dpuAllowanceAndCycle.billingCycleDate ?? 1)
              .toDate(),
          ),
        );
        logger.debug({ label: ns, message: usages });
        yield put<SaveDpuAndDatalakeUsages>({
          type: 'saveDpuAndDatalakeUsages',
          payload: { dpuAndDatalakeUsages: usages },
        });
      } catch (e) {
        handleUnknownError(e);
        logger.debug({ label: `${ns}.error`, message: 'there is an error with dpu usage' });
        yield put<SaveDpuAndDatalakeUsages>({
          type: 'saveDpuAndDatalakeUsages',
          payload: { dpuAndDatalakeUsages: null },
        });
      }
    },

    *getDpuAllowanceAndCycle(_, { select, put }) {
      const ns = 'app.models.organization.getDpuAllowanceAndCycle';
      try {
        const organizationId = yield select<ReduxState>((s) => s.organization.currentOrganization);
        assertIsString(organizationId, 'BEEM202405071216');
        const dpuAllowanceAndCycle = yield* sgcall(() =>
          Organization.getDpuAllowanceAndBilling(organizationId),
        );
        logger.debug({ label: ns, message: dpuAllowanceAndCycle });
        yield put<SaveDpuAllowanceAndCycle>({
          type: 'saveDpuAllowanceAndCycle',
          payload: { dpuAllowanceAndCycle },
        });
      } catch (e) {
        handleUnknownError(e);
        logger.debug({
          label: `${ns}.error`,
          message: 'there is an error with dpuAllowanceAndCycle',
        });
        yield put<SaveDpuAllowanceAndCycle>({
          type: 'saveDpuAllowanceAndCycle',
          payload: { dpuAllowanceAndCycle: null },
        });
      }
    },
  },

  reducers: {
    resetAll() {
      return getInitialState();
    },
    saveCurrentOrganizationAndWorkspace(state, { payload }) {
      return { ...state, ...payload };
    },
    saveCurrent(state, { payload }) {
      return { ...state, ...payload };
    },
    saveInvokeSetWarehouseRetentionResult(state, { payload }) {
      return { ...state, ...payload };
    },
    saveInvokeCreateFivetranGroupIdIfNotExistResult(state, { payload }) {
      return { ...state, ...payload };
    },
    saveInvokeRemoveUserAccessResult(state, { payload }) {
      return { ...state, ...payload };
    },
    saveDpuAndDatalakeUsages(state, { payload }) {
      return { ...state, ...payload };
    },
    saveDpuAllowanceAndCycle(state, { payload }) {
      return { ...state, ...payload };
    },
  },
};

export default OrganizationModel;
